import React, { useState, useEffect, useMemo, useRef } from "react";
import "../../css/CandidatePortal.css";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { candidateAuthHeader } from "../../utils/candidateAuth";
import host from "../../global.jsx";
import { toast } from "react-toastify";
import Timer from "./Timer";
import { ChevronLeft, ChevronRight, XCircle, Clock, Flag } from "lucide-react";
import { useTheme } from "../../hooks/useTheme";
import DiscardChangesModal from "../../components/shared/DiscardChangesModal";

/**
 * Structural sibling of MCQTestPage.jsx, not a shared component — MCQTestPage
 * hardcodes MCQ-specific endpoints and field names throughout, so this repoints
 * every one of them to the Behavioral routes instead. The serialized-navigation-
 * queue, timer-drives-submit, and resume-from-index patterns are copied wholesale.
 * One real difference: time_taken is computed server-side here, never sent by the
 * client (documentation/behavioral-assessment/behavioral-test-phase1-proposal.md §3).
 */
export default function BehavioralTestPage() {
  const navigate = useNavigate();
  const { interview_id } = useParams();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [questions, setQuestions] = useState([]);   // whole test, fetched once
  const [answers, setAnswers] = useState({});       // { questionIndex: optionId | null }
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);

  const [timeLimit, setTimeLimit] = useState(90);
  const [displayTime, setDisplayTime] = useState(90);
  const [stopTest, setStopTest] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Candidate-private scratch state: the server has no field for it and no report
  // reads it, so marks live in localStorage like the coding page's saved code.
  const MARKS_KEY = `behavioral_marked_${interview_id}`;
  const [markedForReview, setMarkedForReview] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(MARKS_KEY)) || {};
    } catch (err) {
      return {};
    }
  });

  // Navigation writes are serialized: a burst of tile clicks must not land out of
  // order, or currentQuestionIndex ends up pointing at the wrong question.
  const navQueueRef = useRef(Promise.resolve());

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const startDateTime = new Date().toISOString();
        const { data: timing } = await axios.get(`${host}/get_behavioral_timelimit`, {
          params: { interview_id, startDateTime },
          headers: candidateAuthHeader(interview_id),
        });

        const timeLimitInSeconds = (timing.behavioral_time_limit || 0) * 60;
        const startTime = new Date(timing.behavioral_start_time);
        const elapsedSeconds = Math.floor(Date.now() / 1000) - Math.floor(startTime.getTime() / 1000);
        const remaining = timeLimitInSeconds - elapsedSeconds;
        setTimeLimit(remaining);
        setDisplayTime(remaining);

        if (timing.behavioral_status === "Complete") {
          setStopTest(true);
          return;
        }

        const { data: all } = await axios.get(`${host}/get_all_behaviorals/${interview_id}`, {
          headers: candidateAuthHeader(interview_id),
        });
        setQuestions(all.questions);
        setTotalQuestions(all.total_questions);

        const seeded = {};
        all.questions.forEach((q) => {
          seeded[q.questionIndex] = q.selected_option_id ?? null;
        });
        setAnswers(seeded);

        const resumeIndex = Math.min(
          Math.max(timing.currentQuestionIndex ?? 0, 0),
          Math.max(all.total_questions - 1, 0)
        );
        setCurrentQuestionIndex(resumeIndex);
      } catch (error) {
        toast.error("Failed to fetch candidate status");
        console.error(error);
      }
    };
    bootstrap();
  }, [interview_id]);

  const answeredCount = useMemo(
    () => Object.values(answers).filter((v) => v != null).length,
    [answers]
  );
  const unansweredCount = totalQuestions - answeredCount;

  useEffect(() => {
    localStorage.setItem(MARKS_KEY, JSON.stringify(markedForReview));
  }, [MARKS_KEY, markedForReview]);

  // Filtered on totalQuestions so a stale key can't count an index past the end.
  const markedCount = useMemo(
    () => Object.keys(markedForReview).filter((i) => markedForReview[i] && Number(i) < totalQuestions).length,
    [markedForReview, totalQuestions]
  );

  const isMarked = !!markedForReview[currentQuestionIndex];
  const toggleMarkForReview = () =>
    setMarkedForReview((prev) => {
      const next = { ...prev };
      if (next[currentQuestionIndex]) delete next[currentQuestionIndex];
      else next[currentQuestionIndex] = true;
      return next;
    });

  const currentQuestion = questions[currentQuestionIndex] || null;
  // Derived, not stored — the option list can never fall out of sync with the
  // question on screen.
  const selectedOption = answers[currentQuestionIndex] ?? null;

  // Behavioural time limits come from time_limit_minutes and can exceed an hour, so
  // the clock has to be able to grow a third field rather than wrapping at 60.
  const formatClock = (seconds) => {
    const total = Math.max(0, seconds);
    const h = Math.floor(total / 3600);
    const mm = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
    const ss = String(total % 60).padStart(2, "0");
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
  };

  const selectOption = (optionId) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQuestionIndex]: prev[currentQuestionIndex] === optionId ? null : optionId,
    }));
  };

  /* ── The one way to move: tiles, Prev and Next all come through here ── */
  const goToQuestion = (targetIndex) => {
    if (targetIndex === currentQuestionIndex) return;
    if (targetIndex < 0 || targetIndex >= totalQuestions) return;

    const fromIndex = currentQuestionIndex;
    const answerToSave = answers[fromIndex] ?? null;

    // Move locally first — everything needed is already in memory.
    setAnswers((prev) => ({ ...prev, [fromIndex]: answerToSave }));
    setCurrentQuestionIndex(targetIndex);

    navQueueRef.current = navQueueRef.current
      .catch(() => {})
      .then(() =>
        axios.get(`${host}/get_behaviorals/${interview_id}`, {
          params: {
            targetIndex,
            fromIndex,
            // Omitted rather than sent null: an absent selectedOptionId is how the
            // server is told to clear the departing answer.
            ...(answerToSave != null ? { selectedOptionId: answerToSave } : {}),
          },
          headers: candidateAuthHeader(interview_id),
        })
      )
      .catch((error) => {
        // The server enforces the time window on every navigation call, not just at
        // final submit — a 409 here means time actually ran out, not a network blip.
        // Submit whatever's answered instead of leaving the candidate on an error
        // toast that implies a retry would help.
        if (error?.response?.status === 409) {
          handleSubmit();
          return;
        }
        console.error("Error saving navigation:", error);
        toast.error("Couldn't save your answer — check your connection");
      });
  };

  const handleSubmit = async () => {
    // save_behavioraltest_result lands the final answer on the server's stored
    // index, so any pending navigation write has to settle first.
    await navQueueRef.current.catch(() => {});

    try {
      await axios.post(`${host}/save_behavioraltest_result`, null, {
        params: { interview_id, selectedOptionId: selectedOption },
        headers: candidateAuthHeader(interview_id),
      });
      localStorage.removeItem(MARKS_KEY);
      toast.success("Test submitted successfully!");
      setTimeout(() => navigate(`/candidate/${interview_id}/testhomepage`), 2000);
    } catch (error) {
      toast.error("Error Submitting Test");
    }
  };

  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const canGoBack = currentQuestionIndex > 0;
  const isTimeUrgent = displayTime <= 60;

  /* ── Stop test screen ── */
  if (stopTest) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-6 ${!isDark ? "bg-[#FAF9F6]" : ""}`}>
        <div className={`rounded-2xl p-10 max-w-md w-full text-center shadow-xl ${isDark ? "glass-effect" : "bg-white border border-gray-200"}`}>
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className={`text-xl font-bold mb-3 ${isDark ? "text-white" : "text-gray-800"}`}>
            Test Already Completed
          </h2>
          <p className={`text-sm leading-relaxed ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            This test has already been completed. Only one attempt is allowed.
          </p>
          <p className={`text-sm mt-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            You can close this window now.
          </p>
        </div>
      </div>
    );
  }

  /* ── Main test UI ── */
  // The platform accent: the same pink→blue as .light .button, .knockout-tile and
  // .border-btn. Held locally rather than via className="button", which has no .dark
  // rule and falls back to the legacy purple for anyone on a dark OS.
  const BRAND_GRADIENT = "linear-gradient(to right, #ec4899, #3b82f6)";

  // One source of truth for the grid, the legend and the submit dialog. Filled states,
  // no outlines: the ring had every unanswered tile competing with the current one.
  const tileClass = (state) =>
    state === "current"
      ? "text-white shadow-sm"
      : state === "answered"
        ? "bg-green-100 text-green-800 dark:bg-green-500/30 dark:text-green-300"
        : "bg-white text-gray-500 shadow-sm dark:bg-white/[0.06] dark:text-gray-400";

  // Layered over the tile's own colour rather than replacing it: "marked" and
  // "answered" are independent facts, and both matter at review time.
  const markDot = <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500" />;

  
  const CURRENT_TILE = { backgroundImage: "linear-gradient(135deg, #ec4899, #3b82f6)" };

  
  const RADIO_RING = {
    background: BRAND_GRADIENT,
    padding: 2,
    boxSizing: "border-box",
    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
  };

  return (
    <>
      <Timer timeLimit={timeLimit} onTimeUp={handleSubmit} onTick={setDisplayTime} showUI={false} />

      <div className={`flex flex-col h-full overflow-hidden ${!isDark ? "bg-[#FAF9F6]" : ""}`}>
        {/* Edge to edge: a timed assessment is the whole context, not a dialog
            floating over one. Dark mode keeps the glass so the branded
            app-background still reads through. */}
        <div className={`flex-1 flex flex-col min-h-0 overflow-hidden ${
          isDark ? "glass-effect backdrop-blur-lg" : "bg-white"
        }`}>

          {/* ── Card body ── */}
          <div className="flex flex-1 min-h-0">

            {/* Main content — a scrolling question column with its own action bar, so
                the controls sit under the options they act on. */}
            <div className="flex-1 flex flex-col min-h-0">

              <div className="flex-1 overflow-y-auto px-8 py-6 flex flex-col">
                {/* Weighted spacers rather than my-auto: auto margins can only split the
                    free space evenly, and 3/7 sits the question above the optical centre.
                    Both are flex-basis 0, so once the content outgrows the column there is
                    no free space to hand out — they stay at zero and it scrolls normally
                    instead of having its top clipped. */}
                <div aria-hidden className="flex-[3]" />
                {currentQuestion ? (
                  <div className="space-y-5 max-w-[46.75rem] w-full mx-auto flex-shrink-0">
                    <div className="flex items-baseline gap-3">
                      <span
                        className={`w-16 flex-shrink-0 text-right text-lg font-bold leading-snug ${
                          isDark ? "text-white" : "text-gray-800"
                        }`}
                      >
                        Q{currentQuestionIndex + 1}.
                      </span>
                      <div className="flex-1 min-w-0 pl-4 border-l-2 border-[#ec4899]">
                        <p className={`text-lg font-semibold leading-snug whitespace-pre-wrap ${isDark ? "text-white" : "text-gray-800"}`}>
                          {currentQuestion.question}
                        </p>
                        {/* The one thing MCQ has no equivalent for: a behavioural item is
                            a situation, not a question, so without this line there is no
                            ask on screen. Kept as a label rather than a sentence so it
                            doesn't read as part of the scenario. */}
                        <p className={`text-xs font-semibold uppercase tracking-wide mt-3 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                          What would you most likely do?
                        </p>
                      </div>
                    </div>

                    {/* Options — a list of any length, selected by option id */}
                    <div className="space-y-3 pl-[4.75rem]">
                      {(currentQuestion.options || []).map((opt) => {
                        const isSelected = selectedOption === opt.id;

                        return (
                          <div
                            key={opt.id}
                            onClick={() => selectOption(opt.id)}
                            className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200 select-none border-2 ${
                              isSelected
                                ? isDark
                                  ? "border-[#736CCF] bg-[rgba(115,108,207,0.18)] shadow-sm"
                                  : "border-[#736CCF] bg-[#E9E8F9] shadow-sm"
                                : isDark
                                  ? "border-white/10 bg-white/5 hover:border-[rgba(115,108,207,0.45)] hover:bg-[rgba(115,108,207,0.06)]"
                                  : "border-gray-200 bg-white hover:border-[rgba(115,108,207,0.45)] hover:bg-[rgba(115,108,207,0.04)]"
                            }`}
                          >

                            <span
                              className={`relative w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center transition-all duration-200 ${
                                isSelected ? "" : isDark ? "border-2 border-white/25" : "border-2 border-gray-300"
                              }`}
                            >
                              {isSelected && (
                                <>
                                  <span className="absolute inset-0 rounded-full" style={RADIO_RING} />
                                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: BRAND_GRADIENT }} />
                                </>
                              )}
                            </span>
                            {/* Dark mode lightens the selected purple to #A9A3E8 — #736CCF is only ~3.5:1 on the glass. */}
                            <span
                              className={`text-sm leading-relaxed flex-1 ${isSelected ? "font-semibold" : "font-medium"}`}
                              style={{ color: isSelected ? (isDark ? "#A9A3E8" : "#736CCF") : isDark ? "#d1d5db" : "#374151" }}
                            >
                              {opt.text}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center flex-shrink-0 gap-3">
                    <div className="w-10 h-10 rounded-full border-2 border-transparent border-t-pink-500 border-r-blue-500 animate-spin" />
                    <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-400"}`}>Loading question…</p>
                  </div>
                )}

                {/* ── Action bar ── */}
                {/* Sticky, not pinned to the viewport: with the question at 30/70 a bar
                    glued to the bottom of the screen sits a long way under the last
                    option it acts on. In flow it hugs the options */}
                {currentQuestion && (
                  <div className={`sticky bottom-0 flex-shrink-0 max-w-[46.75rem] w-full mx-auto mt-14 pl-[4.75rem] ${
                    isDark ? "bg-[#1d0f2d]" : "bg-white"
                  }`}>
                    {/* The fill spans the whole block but the rule only the options' width:
                        a border-t on the padded outer would run 4.75rem past their left edge. */}
                    <div className={`pt-4 pb-4 border-t flex items-center gap-3 ${
                      isDark ? "border-white/15" : "border-gray-200"
                    }`}>
                      {/* The platform secondary, mirroring the job-role wizard's Back.
                          .border-btn-dark in dark rather than .button, which has no .dark
                          rule and falls back to the legacy purple. Enabled only:
                          .border-btn:disabled draws a near-black ring, and .border-btn-dark
                          has no disabled rule at all. !py-2.5 beats the class's own 8px so
                          all four buttons in this row stay one height. */}
                      <button
                        onClick={() => goToQuestion(currentQuestionIndex - 1)}
                        disabled={!canGoBack}
                        title="Previous question"
                        className={`py-2.5 rounded-full text-sm font-medium flex items-center gap-1 transition-all duration-150 ${
                          canGoBack
                            ? isDark
                              ? "border-btn-dark !py-2.5"
                              : "border-btn !text-textSecondary !py-2.5"
                            : `px-4 border ${
                                isDark
                                  ? "border-white/5 text-gray-600 cursor-not-allowed"
                                  : "border-gray-100 text-gray-300 cursor-not-allowed"
                              }`
                        }`}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Prev
                      </button>
                      <div className="flex-1 flex items-center justify-center gap-2">
                        <button
                          onClick={toggleMarkForReview}
                          title="Flag this question to come back to before you submit"
                          className={`px-4 py-2.5 rounded-full text-sm font-medium border flex items-center gap-1.5 transition-all duration-150 ${
                            isMarked
                              ? isDark
                                ? "border-amber-400/40 text-amber-400 bg-amber-500/10"
                                : "border-amber-300 text-amber-600 bg-amber-50"
                              : isDark
                                ? "border-white/20 text-gray-200 hover:bg-white/10"
                                : "border-gray-300 text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          <Flag className={`w-4 h-4 ${isMarked ? "fill-current" : ""}`} />
                          {isMarked ? "Marked for review" : "Mark for review"}
                        </button>
                        <button
                          onClick={() => selectOption(selectedOption)}
                          disabled={selectedOption == null}
                          title="Clear your answer to this question"
                          className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all duration-150 ${
                            selectedOption == null
                              ? isDark
                                ? "border-white/5 text-gray-600 cursor-not-allowed"
                                : "border-gray-100 text-gray-300 cursor-not-allowed"
                              : isDark
                                ? "border-white/20 text-gray-200 hover:bg-white/10"
                                : "border-gray-300 text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          Clear selection
                        </button>
                      </div>

                      
                      <button
                        onClick={() =>
                          isLastQuestion ? setShowConfirm(true) : goToQuestion(currentQuestionIndex + 1)
                        }
                        title={isLastQuestion ? "Review and submit your test" : "Save answer and go to next question"}
                        className="px-5 py-2.5 rounded-full text-sm font-semibold flex items-center gap-1 transition-opacity hover:opacity-90"
                        style={{ background: BRAND_GRADIENT, color: "#fff" }}
                      >
                        {isLastQuestion ? "Review & Submit" : "Next"}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                <div aria-hidden className="flex-[7]" />
              </div>
            </div>

            {/* ── Sidebar — navigation and status only ── */}
            {/* A tint, not just a hairline: at full-bleed width a 1px line can't
                carry the boundary between the work area and the rail. */}
            <div className={`w-72 flex-shrink-0 border-l flex flex-col ${
              isDark ? "border-white/15 bg-white/[0.03]" : "border-gray-200 bg-gray-50"
            }`}>

              {/* Outside the scroll container: in a timed test the clock must never be
                  something you have to scroll back to. */}
              <div className={`flex-shrink-0 px-5 py-4 border-b ${isDark ? "border-white/15" : "border-gray-200"}`}>
                <p className={`text-[11px] font-semibold uppercase tracking-widest mb-1.5 ${
                  isTimeUrgent ? "text-red-500" : isDark ? "text-gray-400" : "text-gray-500"
                }`}>
                  Time remaining
                </p>
                {/* White, not gray-100: the rail is tinted gray-50, so only white lifts
                    off it. The urgent state takes the label with it — the clock sits in
                    peripheral vision now, so the whole block has to change. */}
                <div className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg transition-all duration-300 ${
                  isTimeUrgent
                    ? "bg-red-500 text-white animate-pulse"
                    : isDark
                      ? "bg-white/10 text-white border border-white/15"
                      : "bg-white text-gray-800 border border-gray-200"
                }`}>
                  <Clock className={`w-5 h-5 flex-shrink-0 ${isTimeUrgent ? "text-white" : isDark ? "text-pink-400" : "text-pink-500"}`} />
                  {/* tabular-nums matters at 24px: proportional digits visibly jitter. */}
                  <span className="text-2xl font-bold tabular-nums leading-none">{formatClock(displayTime)}</span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div>
                  <p className={`text-[11px] font-semibold uppercase tracking-widest mb-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Questions
                  </p>
                  <div className="grid grid-cols-5 gap-2">
                    {Array.from({ length: totalQuestions }).map((_, qId) => {
                      const isAnswered = answers[qId] != null;
                      const state = qId === currentQuestionIndex ? "current" : isAnswered ? "answered" : "todo";
                      return (
                        <button
                          key={qId}
                          type="button"
                          onClick={() => goToQuestion(qId)}
                          title={`Question ${qId + 1}${isAnswered ? " — answered" : " — not answered"}${markedForReview[qId] ? " — marked for review" : ""}`}
                          className={`relative w-full h-9 rounded-md flex items-center justify-center text-xs font-semibold transition-colors ${tileClass(state)}`}
                          style={state === "current" ? CURRENT_TILE : undefined}
                        >
                          {qId + 1}
                          {markedForReview[qId] && markDot}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Legend and tally are one thing now: the count sits inside the swatch
                    that explains it, so the key can't drift from the grid above. */}
                <div className={`pt-4 border-t space-y-2 ${isDark ? "border-white/15" : "border-gray-200"}`}>
                  {[
                    { key: "answered", tile: "answered", count: answeredCount, label: "Answered" },
                    { key: "todo", tile: "todo", count: unansweredCount, label: "Not answered" },
                    { key: "marked", tile: "todo", count: markedCount, label: "Marked for review" },
                  ].map(({ key, tile, count, label }) => (
                    <div key={key} className="flex items-center gap-2.5">
                      <div
                        className={`relative w-9 h-9 rounded-md flex items-center justify-center text-xs font-semibold tabular-nums ${tileClass(tile)}`}
                      >
                        {count}
                        {key === "marked" && markDot}
                      </div>
                      <span className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              
              {currentQuestion && (
                <div className={`flex-shrink-0 p-4 border-t ${isDark ? "border-white/15" : "border-gray-200"}`}>
                  <button
                    onClick={() => setShowConfirm(true)}
                    className={`w-full py-2.5 rounded-full text-sm font-semibold border transition-all duration-200
                      ${isDark ? "border-white/20 text-gray-200" : "border-gray-300 text-gray-600"}
                      hover:text-white hover:border-transparent`}
                    style={{
                      background: "transparent",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = BRAND_GRADIENT;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    Submit Test
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* The rail's own tiles rather than prose: the candidate has been reading these
          three shapes for the length of the test, so the counts land without parsing a
          sentence. Spans, not divs — the modal renders this inside a <p>. */}
      <DiscardChangesModal
        isOpen={showConfirm}
        title="Submit the test?"
        message={
          unansweredCount > 0 || markedCount > 0 ? (
            <span className="block text-base">
              <span className="block space-y-2.5 mb-5">
                {[
                  { tile: "answered", count: answeredCount, label: "Answered" },
                  { tile: "todo", count: unansweredCount, label: "Not answered" },
                  ...(markedCount > 0
                    ? [{ tile: "todo", count: markedCount, label: "Marked for review", mark: true }]
                    : []),
                ].map((row) => (
                  <span key={row.label} className="flex items-center gap-3">
                    <span className={`relative w-9 h-9 rounded-md flex items-center justify-center text-xs font-semibold tabular-nums ${tileClass(row.tile)}`}>
                      {row.count}
                      {row.mark && markDot}
                    </span>
                    <span className="text-sm">{row.label}</span>
                  </span>
                ))}
              </span>
              <span className="block text-sm">
                {"You can't change your answers after submitting."}
              </span>
            </span>
          ) : (
            "All questions are answered. You can't change your answers after submitting."
          )
        }
        cancelLabel="Cancel"
        confirmLabel="Yes, Submit"
        confirmVariant="solid"
        onCancel={() => setShowConfirm(false)}
        onConfirm={() => { setShowConfirm(false); handleSubmit(); }}
      />
    </>
  );
}
